import { useEffect, useState, useRef } from 'react';
import './styles.scss';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateHeader } from '../../slices/header-slice';
import { formatDate } from '../../utility/utils';
import SEO from '../../components/seo';
import { getNews } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';
import { useInView } from 'framer-motion';

const News = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [newsItems, setNewsItems] = useState([]);
    const hasFetched = useRef(false);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.1 });

    // Register loading state with global spinner
    useDataLoading('news', isDataLoading, []);

    useEffect(() => {
        if (isInView) {
            dispatch(updateHeader('transparent'));
        } else {
            dispatch(updateHeader('light'));
        }
    }, [isInView, dispatch]);

    useEffect(() => {
        dispatch(updateHeader('transparent'));
    }, [dispatch]);

    useEffect(() => {
        const fetchNewsContent = async () => {
            // Check if we already have news items or if we're already fetching
            if (newsItems.length > 0 || hasFetched.current) return;

            try {
                hasFetched.current = true;
                setIsDataLoading(true);

                // Fetch news items
                const newsResponse = await getNews();
                const news = newsResponse.data || [];

                // Sort by date
                const sortedNews = news.sort((a, b) => new Date(b.date) - new Date(a.date));

                setNewsItems(sortedNews);
            } catch (error) {
                console.error('Error fetching news:', error);
                setNewsItems([]);
            } finally {
                setIsDataLoading(false);
            }
        };

        fetchNewsContent();
    }, [newsItems.length]);

    const onNewsItemClick = (newsItem) => {
        if (!newsItem?.id) return; // Safety check

        // If there's a ctaUrl, open it in a new window
        if (newsItem.ctaUrl) {
            window.open(newsItem.ctaUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const renderImage = (newsItem) => {
        if (!newsItem) return null; // Safety check

        if (newsItem.images?.[0]?.fileName) {
            // For news items, use the optimized image API
            return <img alt={newsItem.title || 'News item image'} src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/news/${newsItem.images[0].fileName}&width=350&height=230&quality=99&format=webp`} />;
        }
        return null;
    };


    return (
        <div className="news-page">
            <SEO
                title="Paramount Wood Co. - News"
                description="A collection of historic news and insights from Paramount Wood Co. descending in chronological order. If you wish to receive these articles in real time, please subscribe to our newsletter for regular updates and happenings from the studio."
                name="Paramount Wood Co."
                type="website"
                url="https://paramountwoodco.com/news"
            />

            <div ref={ref} className="page-hero half">
                <div className="hero-content">
                    <h1>Paramount in the News</h1>
                </div>
            </div>

            <div className='content'>
                <div className='news-content'>
                    {newsItems && newsItems.length > 0 ? (
                        newsItems.map((newsItem, i) => (
                            <div key={newsItem?.id || i} className='news-item'>
                                <div className='item-info'>
                                    <div>
                                        <span className='date'>{formatDate(newsItem?.date)}</span>
                                        <h2>{newsItem?.title || 'Untitled'}</h2>
                                        <div className='html-text' dangerouslySetInnerHTML={{ __html: newsItem?.body || '' }} />

                                    </div>
                                    {(newsItem?.ctaText || newsItem?.ctaUrl) && (
                                        <button
                                            className={`cta gold-fill`}
                                            onClick={() => onNewsItemClick(newsItem)}
                                        >
                                            {newsItem?.ctaText || 'Read More'}
                                        </button>
                                    )}
                                </div>

                                <div className='item-image'>
                                    {renderImage(newsItem)}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-content">
                            <p>No news content available at the moment.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default News;
